import * as React from 'react'
import { ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
    Button,
    Card,
    Divider,
    makeStyles,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    MessageBar,
    MessageBarBody,
    tokens,
    Tooltip,
} from '@fluentui/react-components'
import {
    AddRegular,
    bundleIcon,
    CheckmarkStarburstFilled,
    CheckmarkStarburstRegular,
    DocumentOnePageSparkleFilled,
    DocumentOnePageSparkleRegular,
    OpenRegular,
    TimelineFilled,
    TimelineRegular,
} from '@fluentui/react-icons'
import useEntity from '../../../../hooks/useEntity'
import { ViewFileProps } from '../ViewFile'
import Navigation, { NavigationState } from '../../../../components/Form/Navigation'
import GlobalContext, { AIAgentEnum } from '../../../../context/GlobalContext'
import { useApplicationStyles } from '../../../../pages/App'
import Loading from '../../../../components/Loading'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import useAxios from '../../../../hooks/useAxios'
import AuthorizationContext from '../../../../context/AuthorizationContext'
import { useSettings } from '../../../../hooks/useSettings'
import useDelayedInterval from '../../../../hooks/useDelayedInterval'
import useAwaitGeneratedEntity from '../../../../hooks/useAwaitGeneratedEntity'
import { Destination } from '../../../../context/AxiosContext'
import useUser from '../../../../hooks/useUser'
import { formatDateTime } from '../../../../helpers/dateHelper'
import { useMount } from '@fluentui/react-hooks'
import SubmittingButton from '../../../components/Button/SubmittingButton'
import { FileAIAgentSummary } from './FileAIAgentSummary'
import { FileAIAgentTimeline } from './FileAIAgentTimeline'

export type FileAIAgentDto = {
    id: string
    created: DateTime
    fileId: string
    userId: string
    type: AIAgentEnum
    externalId: string
}

export type AIAgentResultDto<ResultT> = {
    error: object
    warning: string
    responseType: object
    data: ResultT
}

export type AIAgentProjectDto = {
    id: string
    name: string
    description: string
    legalArea: string
    legalSubArea: string
    createdAt: DateTime
    projectType: string
    isAnonymizationEnabled: boolean
}

export type AIAgentConsentDto = {
    consentUrl: string
}

export type AIAgentConsentStatusDto = {
    organisationId: string
    consentGranted: boolean
    consentGrantedAt: string
}

export type AIAgentProjectStatusDto = {
    id: string
    hasDocuments: string
    active: boolean
    anonymized: boolean
    anonymizationReviewRequired: boolean
}

export type AIAgentInteractionDto = {
    id: string
    type: AIAgentInteractionEnum
    title: string
}

export type AIAgentSummaryDto = AIAgentInteractionDto & {
    versions: AIAgentSummaryVersionDto[]
    preferredVersion: AIAgentSummaryVersionDto
    documents: object[]
    modifiedAt: string
    projectId: string
    userWarnings: object[]
}

export type AIAgentSummaryVersionDto = {
    content: string
    timestamp: string
    versionNumber: number
}

export type AIAgentTimelineEventDto = {
    id: string
    date: string
    time: string
    description: string
    type: string
    parties: string[]
    relevance: string
    remark: string
    documentName: string
    page: number
    paragraph: number
    priority: string
    tags: string[]
    createdBy: string
    createdDateTime: string
    updatedBy: string
    updatedDateTime: string
}

export type AIAgentTimelineDto = AIAgentInteractionDto & {
    orderPreference: string
    includedDocumentIds: string[]
    events: AIAgentTimelineEventDto[]
    hasNewDocuments: boolean
    status: string
}

export enum AIAgentInteractionEnum {
    Timeline = 'TIMELINE',
    Summary = 'SUMMARY',
}

export type FileAIAgentProps = {
    agent?: AIAgentEnum
}

export type LegalPAToken = {
    userId: string
    organisationId: string
    userToken: string
    userTokenExpiresAt: string
    refreshToken: string
}

// Generating an interaction is asynchronous: the generate call answers long before LegalPA has
// written the result, so we poll the list until an id shows up that we did not know yet.
const INTERACTION_POLL_INTERVAL = 5_000
const INTERACTION_POLL_ATTEMPTS = 60

const CheckmarkStarburst = bundleIcon(CheckmarkStarburstFilled, CheckmarkStarburstRegular)
const TimelineIcon = bundleIcon(TimelineFilled, TimelineRegular)
const SummaryIcon = bundleIcon(DocumentOnePageSparkleFilled, DocumentOnePageSparkleRegular)

const useStyles = makeStyles({
    container: {
        display: 'grid',
        gap: tokens.spacingHorizontalM,
    },
    containerColumn: {
        gridTemplateColumns: '150px 1fr',
    },
    containerRow: {
        gridTemplateRows: '1fr',
    },
    sidebar: {
        display: 'flex',
        minWidth: 'max-content',
        flexWrap: 'nowrap',
        gap: tokens.spacingHorizontalM,
    },
    sidebarColumn: {
        flexDirection: 'column',
    },
    sidebarRow: {
        flexDirection: 'row-reverse',
        alignItems: 'center',
        marginBottom: tokens.spacingVerticalM,
    },
    createButtonRow: {
        marginRight: tokens.spacingHorizontalL,
    },
    createButtonColumn: {
        marginLeft: tokens.spacingHorizontalM,
    },
    gapXS: {
        gap: tokens.spacingHorizontalXS,
    },
})

export function FileAIAgentOverview(props: Partial<FileAIAgentProps> & ViewFileProps): ReactElement {
    const appStyles = useApplicationStyles()
    const axios = useAxios()
    const { host } = useContext(AuthorizationContext)
    const globalContext = useContext(GlobalContext)
    const { currentUser } = useUser()
    const { settings } = useSettings()
    const styles = useStyles()
    const { t: translate } = useTranslation()

    /**
     * Interface state
     */
    const [selected, setSelected] = useState<NavigationState>()
    const [currentAIAgent, setCurrentAIAgent] = useState<FileAIAgentDto>()
    const [syncDisabled, setSyncDisabled] = useState<boolean>(false)
    // A LegalPA project is only visible to its collaborators, while in HAMMOCK everybody with
    // access to the file may see it. Remember which project we already retried for, so opening a
    // file adds the user at most once even when the token refresh restarts the whole chain.
    const addedCollaboratorFor = useRef<string>()
    const [addingCollaborator, setAddingCollaborator] = useState<boolean>(false)

    useMount(() => {
        // Without a configured agent there is nothing to look up; skip the call instead of
        // requesting /ai-agent/undefined, which the backend cannot bind to AIAgentEnum.
        if (!props.agent) {
            return
        }

        axios
            .get<FileAIAgentDto>(`/file/${props.file.id}/ai-agent/${props.agent}`, {
                headers: { Destination: Destination.Hammock, SuppressErrors: 'true' },
            })
            // 204 (no agent linked yet) resolves with an empty body; normalize to undefined.
            .then(aiAgent => setCurrentAIAgent(aiAgent.data || undefined))
    })

    /**
     * LegalPA state main state
     */
    // const [,] = useEntity<any[]>(
    //     () => ({
    //         url: `/ai-agent/legalpa/user`,
    //         baseURL: process.env.API_HOST,
    //         headers: { Destination: Destination.Hammock, SuppressErrors: 'true' },
    //     }),
    //     props.agent || null,
    //     result => result.data,
    // )

    const [consentStatus] = useEntity<AIAgentConsentStatusDto>(
        () => ({
            url: `/ai-agent/legalpa/consent-status/${host.RowKey}`,
            baseURL: process.env.API_HOST,
            headers: { Destination: Destination.Hammock, SuppressErrors: 'true' },
        }),
        !settings?.legalpa?.consentComplete ? '1' : null,
        result => result.data,
    )
    useEffect(() => {
        if (consentStatus.loaded && settings?.legalpa?.consentComplete !== consentStatus.data.consentGranted) {
            globalContext.reset()
        }
    }, [consentStatus, currentAIAgent])

    const userTokenRequest = () => ({
        url: `/ai-agent/legalpa/${host.RowKey}/user/token`,
        baseURL: process.env.API_HOST,
        headers: { Destination: Destination.Hammock, SuppressErrors: 'true' },
    })
    // Requesting a user token creates the user in LegalPA and sets the organisation on the setting,
    // so only ask for one when this file is already linked. For an unlinked file the create button
    // fetches one at the moment it actually needs it.
    const [currentAIToken, , resetAIToken] = useEntity<LegalPAToken>(userTokenRequest, currentAIAgent?.id || null)
    const { isRunning: isCurrentAITokenRunning, setRunning: setCurrentAITokenRunning } = useDelayedInterval(1_800_000, () => resetAIToken())

    //
    /** All LegalPA API from here **/
    //

    // const [,] = useEntity<any[]>(
    //     () => ({
    //         url: `/api/v1/organizations`,
    //         baseURL: process.env.LEGALPA_API_URL,
    //         headers: { Authorization: `Bearer ${currentAIToken?.data?.userToken}`, SuppressErrors: 'true' },
    //     }),
    //     currentAIToken.loaded ? '1' : null,
    //     result => result.data,
    // )
    // const [,] = useEntity<any[]>(
    //     () => ({
    //         url: `/api/v1/projects`,
    //         baseURL: process.env.LEGALPA_API_URL,
    //         params: { projectTypeCategory: 'HAMMOCK' },
    //         headers: { Authorization: `Bearer ${currentAIToken?.data?.userToken}`, SuppressErrors: 'true' },
    //     }),
    //     currentAIToken.loaded ? '1' : null,
    //     result => result.data,
    // )

    const [consentUrl] = useEntity<AIAgentConsentDto>(
        () => ({
            url: `/api/v1/integrations/hammock/consent-url`,
            baseURL: process.env.LEGALPA_API_URL,
            headers: { Authorization: `Bearer ${currentAIToken?.data?.userToken}`, SuppressErrors: 'true' },
        }),
        consentStatus.loaded && !consentStatus.data.consentGranted ? '1' : null,
        result => result.data,
    )

    // Keep refreshing the token once it has been loaded.
    useEffect(() => {
        if (!isCurrentAITokenRunning && currentAIToken.loaded) {
            setCurrentAITokenRunning(true)
        }
    }, [currentAIToken])

    // Create the LegalPA project for this file and link it to the file.
    const createAIAgent = (): Promise<void> =>
        (currentAIToken.data ? Promise.resolve(currentAIToken.data) : axios.request<LegalPAToken>(userTokenRequest()).then(result => result.data))
            .then(token =>
                axios.post<AIAgentResultDto<AIAgentProjectDto>>(
                    `/api/v1/projects`,
                    {
                        name: settings?.fileSharepointDisplayName
                            .replaceAll(/{{\s*file\.name\s*}}/g, props.file.name)
                            .replaceAll(/{{\s*file\.reference\s*}}/g, props.file.reference)
                            .replaceAll(/[/\\]/g, '-')
                            .trim(),
                        projectTypeId: '29DAE791-21CD-4097-8C9A-2802EC0A16B4',
                        hammockDossierFolderId: props.file.documentRootId,
                        isAnonymizationEnabled: true,
                    },
                    {
                        baseURL: process.env.LEGALPA_API_URL,
                        headers: { Authorization: `Bearer ${token.userToken}`, SuppressErrors: 'true' },
                    },
                ),
            )
            .then(response => axios.post<FileAIAgentDto>(`/file/${props.file.id}/ai-agent`, { type: props.agent, externalId: response.data.data.id }))
            // Linking gives the token entity an id, so it fetches a token of its own once. That
            // second round is accepted: by then the LegalPA user exists and the call is idempotent.
            .then(response => setCurrentAIAgent(response.data))
            .then(() => globalContext.reset())

    const [,] = useEntity<AIAgentProjectDto, AIAgentResultDto<AIAgentProjectDto>>(
        () => ({
            url: `/api/v1/projects/${currentAIAgent.externalId}`,
            baseURL: process.env.LEGALPA_API_URL,
            headers: { Authorization: `Bearer ${currentAIToken?.data?.userToken}`, SuppressErrors: 'true' },
        }),
        currentAIToken?.data ? currentAIAgent?.externalId || null : null,
        result => result.data?.data,
    )
    const [projectStatus, , , reloadProjectStatus] = useEntity<AIAgentProjectStatusDto, AIAgentResultDto<AIAgentProjectStatusDto>>(
        () => ({
            url: `/api/v1/projects/${currentAIAgent.externalId}/status`,
            baseURL: process.env.LEGALPA_API_URL,
            headers: { Authorization: `Bearer ${currentAIToken?.data?.userToken}`, SuppressErrors: 'true' },
        }),
        currentAIToken?.data ? currentAIAgent?.externalId || null : null,
        result => result.data?.data,
    )
    const [,] = useEntity<any, AIAgentResultDto<any>>(
        () => ({
            url: `/api/v1/projects/${currentAIAgent.externalId}/collaborators`,
            baseURL: process.env.LEGALPA_API_URL,
            headers: { Authorization: `Bearer ${currentAIToken?.data?.userToken}`, SuppressErrors: 'true' },
        }),
        currentAIToken?.data ? currentAIAgent?.externalId || null : null,
        result => result.data?.data,
    )
    const [summaries, , , reloadSummaries] = useEntity<AIAgentSummaryDto[], AIAgentResultDto<AIAgentSummaryDto[]>>(
        () => ({
            baseURL: process.env.LEGALPA_API_URL,
            url: `/api/v1/projects/${currentAIAgent.externalId}/summaries`,
            headers: { Authorization: `Bearer ${currentAIToken?.data?.userToken}`, SuppressErrors: 'true' },
        }),
        projectStatus.loaded ? currentAIAgent?.id || null : null,
        result =>
            result.data?.data?.map(item => ({
                ...item,
                type: AIAgentInteractionEnum.Summary,
                title: item.title || formatDateTime(item.modifiedAt),
            })) || [],
    )
    const [timelines, , , reloadTimelines] = useEntity<AIAgentTimelineDto[], AIAgentResultDto<AIAgentTimelineDto>>(
        () => ({
            baseURL: process.env.LEGALPA_API_URL,
            url: `/api/v1/projects/${currentAIAgent.externalId}/timelines`,
            headers: { Authorization: `Bearer ${currentAIToken?.data?.userToken}`, SuppressErrors: 'true' },
        }),
        projectStatus.loaded ? currentAIAgent?.id || null : null,
        result =>
            (result.data?.data ? [result.data.data] : [])?.map(item => ({ ...item, type: AIAgentInteractionEnum.Timeline, title: 'Tijdlijn' })) || [],
    )
    // Not being a collaborator is the normal situation for a colleague of whoever linked the file;
    // anything else (an expired token, a 5xx, a network error) is a real failure that adding a
    // collaborator cannot fix.
    const projectStatusDeniedAccess = projectStatus.rejected && [403, 404].includes(projectStatus.error?.response?.status)

    // Add the user as a collaborator once, then ask for the status again.
    useEffect(() => {
        const projectId = currentAIAgent?.externalId
        if (!projectStatusDeniedAccess || !projectId || addedCollaboratorFor.current === projectId) {
            return
        }

        addedCollaboratorFor.current = projectId
        setAddingCollaborator(true)
        axios
            .post(`/ai-agent/legalpa/${host.RowKey}/file/${props.file.id}/collaborator`, undefined, {
                baseURL: process.env.API_HOST,
                headers: { Destination: Destination.Hammock, SuppressErrors: 'true' },
            })
            .then(() => reloadProjectStatus())
            .catch(() => undefined)
            .finally(() => setAddingCollaborator(false))
    }, [projectStatus])

    const sortedInteractions: AIAgentInteractionDto[] = useMemo(() => {
        return [...(summaries.data || []), ...(timelines.data || [])]
    }, [summaries.data, timelines.data])
    const selectedInteraction: AIAgentInteractionDto = useMemo(() => {
        return sortedInteractions?.find(item => item.id === selected?.id)
    }, [sortedInteractions, selected])

    // Select whatever LegalPA generated as soon as it lands, so the user sees the result.
    const selectGenerated = (generated: AIAgentInteractionDto) => {
        setSelected({ id: generated.id, isNew: false, isDirty: false })
        reloadProjectStatus()
    }
    const generatedSummary = useAwaitGeneratedEntity(
        summaries.data,
        reloadSummaries,
        selectGenerated,
        INTERACTION_POLL_INTERVAL,
        INTERACTION_POLL_ATTEMPTS,
    )
    const generatedTimeline = useAwaitGeneratedEntity(
        timelines.data,
        reloadTimelines,
        selectGenerated,
        INTERACTION_POLL_INTERVAL,
        INTERACTION_POLL_ATTEMPTS,
    )

    if (!props.agent) {
        return (
            <Card className={appStyles.flexRow}>
                De AI module is nog niet ingesteld. Neem contact op met <a href={'https://legalpa.ai'}>LegalPA</a> om een licentie af te sluiten en te
                koppelen met HAMMOCK.
            </Card>
        )
    }

    // A denied status is only a real failure once the collaborator was added and the retry failed
    // too; any other rejection is reported straight away.
    const projectStatusFailed =
        projectStatus.rejected && !addingCollaborator && (!projectStatusDeniedAccess || addedCollaboratorFor.current === currentAIAgent?.externalId)

    if (currentAIToken.rejected || summaries.rejected || timelines.rejected || projectStatusFailed) {
        return (
            <MessageBar intent={'error'}>
                <MessageBarBody>{translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.error.load`)}</MessageBarBody>
            </MessageBar>
        )
    }

    // Summaries and timelines only start once the status is in, so without the first two conditions
    // the tab would render half empty while the status and the collaborator call are still running.
    if (currentAIAgent?.id && (currentAIToken.loading || projectStatus.loading || addingCollaborator)) {
        return <Loading />
    }

    if (summaries.loading || timelines.loading) {
        return <Loading />
    }

    return (
        <>
            <div className={mergeClasses(appStyles.flexColumn, styles.gapXS)}>
                {currentAIAgent?.id === undefined && (
                    <MessageBar intent={'info'} layout={'multiline'}>
                        <MessageBarBody>
                            {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.new.file.intro`)}
                        </MessageBarBody>
                    </MessageBar>
                )}
                {!settings?.legalpa?.organisationId && (
                    <MessageBar intent={'info'} layout={'multiline'}>
                        <MessageBarBody>
                            {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.new.organisation.intro`)}
                        </MessageBarBody>
                    </MessageBar>
                )}
                {!currentUser?.legalPAId && (
                    <MessageBar intent={'info'} layout={'multiline'}>
                        <MessageBarBody>
                            {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.new.account.intro`)}
                        </MessageBarBody>
                    </MessageBar>
                )}
                {settings?.legalpa?.organisationId && settings.legalpa?.consentComplete !== true && (
                    <MessageBar intent={'warning'} layout={'multiline'}>
                        <MessageBarBody>
                            {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.new.consent.intro`)}
                        </MessageBarBody>
                    </MessageBar>
                )}
                {projectStatus?.data?.anonymizationReviewRequired && (
                    <MessageBar intent={'warning'} layout={'multiline'}>
                        <MessageBarBody>
                            {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.anonymize.pending`)}
                        </MessageBarBody>
                    </MessageBar>
                )}
                {generatedSummary.isPending && (
                    <MessageBar intent={'info'} layout={'multiline'}>
                        <MessageBarBody>
                            {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.interaction.summary.pending`)}
                        </MessageBarBody>
                    </MessageBar>
                )}
                {generatedSummary.hasTimedOut && (
                    <MessageBar intent={'warning'} layout={'multiline'}>
                        <MessageBarBody>
                            {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.interaction.summary.pending.timeout`)}
                        </MessageBarBody>
                    </MessageBar>
                )}
                {generatedTimeline.isPending && (
                    <MessageBar intent={'info'} layout={'multiline'}>
                        <MessageBarBody>
                            {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.interaction.timeline.pending`)}
                        </MessageBarBody>
                    </MessageBar>
                )}
                {generatedTimeline.hasTimedOut && (
                    <MessageBar intent={'warning'} layout={'multiline'}>
                        <MessageBarBody>
                            {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.interaction.timeline.pending.timeout`)}
                        </MessageBarBody>
                    </MessageBar>
                )}
            </div>
            <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS)}>
                {!currentAIAgent?.id && (
                    <SubmittingButton appearance={'primary'} onClick={createAIAgent}>
                        {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.new`)}
                    </SubmittingButton>
                )}
                {currentAIAgent?.id && (
                    <Tooltip
                        relationship="description"
                        content={translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.open.tooltip`)}
                        withArrow
                    >
                        <Button
                            appearance="secondary"
                            icon={<OpenRegular />}
                            className={mergeClasses(styles.createButtonColumn)}
                            onClick={() => {
                                window.open(`${process.env.LEGALPA_APP_URL}/dossiers/${currentAIAgent.externalId}?idp_hint=hammock`, '_blank')
                            }}
                        >
                            {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.open`)}
                        </Button>
                    </Tooltip>
                )}

                {currentAIAgent?.id && settings.legalpa?.consentComplete === true && (
                    <SubmittingButton
                        appearance="secondary"
                        icon={<OpenRegular />}
                        disabled={syncDisabled}
                        tooltip={translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.synchronise.tooltip`)}
                        onClick={() =>
                            axios
                                .post<any, AIAgentResultDto<any>>(
                                    `/api/v1/projects/${currentAIAgent.externalId}/hammock-dossier/sync`,
                                    {},
                                    {
                                        baseURL: process.env.LEGALPA_API_URL,
                                        headers: { Authorization: `Bearer ${currentAIToken.data.userToken}`, SuppressErrors: 'true' },
                                    },
                                )
                                .then(() => setSyncDisabled(true))
                                .then(() => reloadProjectStatus())
                        }
                    >
                        {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.synchronise`)}
                    </SubmittingButton>
                )}
                {currentAIAgent?.id && consentStatus.data && !consentStatus.data?.consentGranted && (
                    <Tooltip
                        relationship="description"
                        content={translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.new.consent.tooltip`)}
                        withArrow
                    >
                        <Button
                            appearance="secondary"
                            icon={<OpenRegular />}
                            onClick={() => {
                                window.open(consentUrl.data.consentUrl + '?idp_hint=hammock')
                            }}
                        >
                            {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.new.consent`)}
                        </Button>
                    </Tooltip>
                )}
                {projectStatus?.data?.anonymizationReviewRequired && (
                    <Tooltip
                        relationship="description"
                        content={translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.reviewrequired.tooltip`)}
                        withArrow
                    >
                        <Button
                            appearance="secondary"
                            icon={<OpenRegular />}
                            onClick={() => {
                                window.open(
                                    `${process.env.LEGALPA_APP_URL}/dossiers/${currentAIAgent.externalId}/documents?idp_hint=hammock`,
                                    '_blank',
                                )
                            }}
                        >
                            {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.reviewrequired`)}
                        </Button>
                    </Tooltip>
                )}
            </div>
            <Divider vertical={false} />
            {currentAIAgent?.id && (
                <>
                    <div className={mergeClasses(appStyles.displayFlex, appStyles.gapS)}>
                        <Menu>
                            <MenuTrigger disableButtonEnhancement>
                                <Tooltip
                                    relationship="description"
                                    content={translate(
                                        `file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.interaction.new.tooltip`,
                                    )}
                                    withArrow
                                >
                                    <MenuButton appearance="primary" icon={<AddRegular />} className={mergeClasses(styles.createButtonColumn)}>
                                        {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.interaction.new`)}
                                    </MenuButton>
                                </Tooltip>
                            </MenuTrigger>
                            <MenuPopover>
                                <MenuList>
                                    <MenuItem
                                        onClick={() => {
                                            axios
                                                .post<any, AIAgentResultDto<any>>(
                                                    `/api/v1/projects/${currentAIAgent.externalId}/timelines/generate`,
                                                    {},
                                                    {
                                                        baseURL: process.env.LEGALPA_API_URL,
                                                        headers: {
                                                            Authorization: `Bearer ${currentAIToken.data.userToken}`,
                                                            SuppressErrors: 'true',
                                                        },
                                                    },
                                                )
                                                .then(() => {
                                                    reloadProjectStatus()
                                                    generatedTimeline.start()
                                                    reloadTimelines()
                                                })
                                        }}
                                        disabled={
                                            generatedTimeline.isPending ||
                                            !!sortedInteractions?.find(item => item.type === AIAgentInteractionEnum.Timeline)
                                        }
                                    >
                                        {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.interaction.timeline`)}
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            axios
                                                .post<any, AIAgentResultDto<AIAgentResultDto<string>>>(
                                                    `/api/v1/projects/${currentAIAgent.externalId}/summaries/generate-stream-ws`,
                                                    {},
                                                    {
                                                        baseURL: process.env.LEGALPA_API_URL,
                                                        headers: {
                                                            Authorization: `Bearer ${currentAIToken.data.userToken}`,
                                                            SuppressErrors: 'true',
                                                        },
                                                    },
                                                )
                                                .then(() => {
                                                    reloadProjectStatus()
                                                    generatedSummary.start()
                                                    reloadSummaries()
                                                })
                                        }}
                                        disabled={generatedSummary.isPending}
                                    >
                                        {translate(`file.pivot.aiagent.${props.agent.toLowerCase().replaceAll('_', '-')}.interaction.summary`)}
                                    </MenuItem>
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    </div>
                    <div className={mergeClasses(styles.container, styles.containerColumn, styles.containerRow)}>
                        <Navigation<AIAgentInteractionDto, NavigationState>
                            entities={sortedInteractions}
                            isLoaded={summaries.loaded && timelines.loaded}
                            getEntityKey={entity => entity.id}
                            getEntityId={entity => entity.id}
                            getEntityLabel={entity => entity.title}
                            getEntityIcon={entity =>
                                entity.type === AIAgentInteractionEnum.Timeline ? (
                                    <TimelineIcon />
                                ) : entity.type === AIAgentInteractionEnum.Summary ? (
                                    <SummaryIcon />
                                ) : (
                                    <CheckmarkStarburst />
                                )
                            }
                            selected={selected}
                            setSelected={setSelected}
                            createSelectedState={entity => ({
                                id: entity.id,
                                isNew: false,
                                isDirty: false,
                            })}
                            useColumns={true}
                        />
                        <div>
                            {selected?.id && selectedInteraction?.type === AIAgentInteractionEnum.Summary && (
                                <FileAIAgentSummary {...props} currentAIAgent={currentAIAgent} summary={selectedInteraction as AIAgentSummaryDto} />
                            )}
                            {selected?.id && selectedInteraction?.type === AIAgentInteractionEnum.Timeline && (
                                <FileAIAgentTimeline
                                    {...props}
                                    currentAIAgent={currentAIAgent}
                                    timeline={selectedInteraction as AIAgentTimelineDto}
                                />
                            )}
                        </div>
                    </div>
                </>
            )}
        </>
    )
}
